import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Paper,
  Grid,
  Chip,
  Stack,
  Divider,
  alpha,
  LinearProgress
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  LocationOn as LocationIcon,
  Group as GroupIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
  Description as DescriptionIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Star as StarIcon,
  Language as LanguageIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { api } from '@/services/api';
import { OpportunityApi } from '@/services/api/domains/opportunity';
import type { OpportunityDetailResponse, AIMatchAssessment, ApplicationResponse } from '@/types/models';
import { ROUTES } from '@/config/routes';

const getMatchScoreColor = (score: number): "success" | "warning" | "error" | "info" => {
  const percentage = score * 100;
  if (percentage >= 70) return "success";
  if (percentage >= 40) return "warning";
  return "error";
};

const MatchScoreChip: React.FC<{ score: number }> = ({ score }) => {
  const percentage = Math.round(score * 100);
  const color = getMatchScoreColor(score);
  
  return (
    <Chip
      label={`${percentage}% Match`}
      color={color}
      size="small"
      sx={{ 
        fontWeight: 'medium',
        minWidth: 90,
        '& .MuiChip-label': {
          px: 1
        }
      }}
    />
  );
};

const InfoItem: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}> = ({ icon, label, value }) => (
  <Stack spacing={0.5}>
    <Typography variant="overline" color="text.secondary">
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={1}>
      {icon}
      {typeof value === 'string' ? (
        <Typography variant="body1">{value}</Typography>
      ) : (
        value
      )}
    </Stack>
  </Stack>
);

const formatAddress = (opportunity: {
  street_address?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  country?: string | null;
  organization?: {
    street_address: string | null;
    city: string | null;
    state: string | null;
    postal_code: string | null;
    country: string | null;
  };
}) => {
  // Try opportunity address first
  const opportunityParts = [
    opportunity.street_address,
    opportunity.city,
    opportunity.state,
    opportunity.postal_code,
    opportunity.country
  ].filter(Boolean);

  if (opportunityParts.length > 0) {
    return opportunityParts.join(', ');
  }

  // Fallback to organization address
  if (opportunity.organization) {
    const orgParts = [
      opportunity.organization.street_address,
      opportunity.organization.city,
      opportunity.organization.state,
      opportunity.organization.postal_code,
      opportunity.organization.country
    ].filter(Boolean);
    return orgParts.length > 0 ? orgParts.join(', ') : null;
  }

  return null;
};

// Helper function to get chip color based on location type
const getLocationChipColor = (locationType: string | null | undefined): "success" | "info" | "secondary" => {
  if (!locationType) return "secondary";
  const type = locationType.toLowerCase();
  if (type === "in person") return "success";
  if (type === "hybrid") return "info";
  return "secondary";
};



const OpportunityDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showAIAnalysis, setShowAIAnalysis] = useState(false);

  // Update the query configuration
  const { data: opportunity, isLoading, error } = useQuery<OpportunityDetailResponse>({
    queryKey: OpportunityApi.getOpportunityQueryKey(id!),
    queryFn: async () => {
      if (!id) throw new Error('No opportunity ID provided');
      return api.opportunities.getOpportunityDetail(parseInt(id));
    },
    enabled: !!id,
    retry: 2,
    staleTime: 1000 * 60 * 10, // Cache for 10 minutes
    gcTime: 1000 * 60 * 15, // Keep in cache for 15 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });

  // Check if user has already applied
  const { data: existingApplication } = useQuery<{ exists: boolean; application?: ApplicationResponse }>({
    queryKey: OpportunityApi.getApplicationCheckQueryKey(id!),
    queryFn: async () => {
      if (!id) return { exists: false };
      return api.applications.checkExistingApplication(id);
    },
    enabled: !!id
  });

  // Use react-query for AI match assessment
  const { data: aiMatch, isLoading: isLoadingAI, refetch: refetchAIMatch } = useQuery<AIMatchAssessment>({
    queryKey: ['opportunity-ai-match', id],
    queryFn: () => api.opportunities.getOpportunityAIMatch(parseInt(id!)),
    enabled: false // Don't fetch automatically
  });

  const handleApplicationClick = (applicationId: number) => {
    navigate(ROUTES.STUDENT.APPLICATIONS.DETAIL(applicationId));
  };

  const handleApply = () => {
    if (opportunity) {
      console.log('Storing in cache:', opportunity); // Debug log
      // Store the opportunity data with consistent structure
      queryClient.setQueryData(OpportunityApi.getOpportunityQueryKey(id!), opportunity);
      navigate(ROUTES.STUDENT.APPLICATIONS.NEW(id!));
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const handleAssessFit = async () => {
    setShowAIAnalysis(true);
    try {
      await refetchAIMatch();
    } catch (error) {
      console.error('Error assessing fit:', error);
    }
    setTimeout(() => {
      const aiSection = document.getElementById('ai-analysis-section');
      if (aiSection) {
        aiSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  };


  // Helper function to get color based on score
  const getScoreColor = (score: number): "success" | "warning" | "error" => {
    if (score >= 70) return "success";
    if (score >= 40) return "warning";
    return "error";
  };

  // Helper function to render progress bar
  const renderProgressBar = (score: number) => (
    <LinearProgress
      variant="determinate"
      value={score}
      sx={{ 
        flexGrow: 1,
        height: 8,
        borderRadius: 4,
        bgcolor: 'background.paper',
        '& .MuiLinearProgress-bar': {
          borderRadius: 4,
          bgcolor: theme => theme.palette[getScoreColor(score)].main
        }
      }}
    />
  );

  // Helper function to render score section
  const renderScoreSection = (
    label: string,
    score: number,
    analysis: string
  ) => (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
          {label}:
        </Typography>
        {renderProgressBar(score)}
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
          {Math.round(score)}%
        </Typography>
      </Box>
      <Typography variant="body2" color="text.secondary" mt={0.5} ml={13}>
        {analysis}
      </Typography>
    </Box>
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !opportunity) {
    return (
      <Alert severity="error">
        Failed to load opportunity details. Please try again later.
      </Alert>
    );
  }

  return (
    <Box sx={{ pb: 2 }}>
      {/* Header Section */}
      <Box mb={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box>
            <Button
              onClick={handleBack}
              startIcon={<ArrowBackIcon />}
              sx={{ mb: 2 }}
            >
              Back
            </Button>
            <Typography variant="h4" gutterBottom={false} fontWeight="medium">
              {opportunity?.title}
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ mt: 0.5 }}>
              {opportunity?.organization?.name}
            </Typography>
          </Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            {opportunity?.match_details && (
              <MatchScoreChip score={opportunity.match_details.overall_score} />
            )}
            <Button
              variant="contained"
              onClick={handleAssessFit}
              disabled={isLoadingAI}
              startIcon={<StarIcon />}
              size="small"
            >
              {isLoadingAI ? 'Analyzing...' : 'AI Feedback'}
            </Button>
          </Stack>
        </Stack>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} md={8}>
          <Stack spacing={2}>
            {/* Description */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6">
                  About this Opportunity
                </Typography>
              </Stack>
              <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                {opportunity?.description}
              </Typography>
            </Paper>

            {/* Requirements */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <CheckCircleIcon color="primary" />
                <Typography variant="h6">
                  Required Skills
                </Typography>
              </Stack>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {opportunity?.required_skills?.map((skill) => (
                  <Chip
                    key={skill}
                    label={skill}
                    sx={{ m: 0.5 }}
                  />
                ))}
              </Stack>
            </Paper>

            {/* Organization Info */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <BusinessIcon color="primary" />
                <Typography variant="h6">
                  About the Organization
                </Typography>
              </Stack>
              
              <Stack spacing={1.5}>
                {opportunity?.organization?.description && (
                  <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                    {opportunity.organization.description}
                  </Typography>
                )}
                
                <Grid container spacing={2}>
                  {opportunity?.organization?.industry && (
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="overline" color="text.secondary">
                          Industry
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <BusinessIcon fontSize="small" color="action" />
                          <Typography>{opportunity.organization.industry}</Typography>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                  
                  {opportunity?.organization?.website && (
                    <Grid item xs={12} sm={6}>
                      <Stack spacing={0.5}>
                        <Typography variant="overline" color="text.secondary">
                          Website
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <LanguageIcon fontSize="small" color="action" />
                          <Button
                            href={opportunity.organization.website}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ 
                              textTransform: 'none',
                              fontWeight: 'normal',
                              p: 0,
                              minWidth: 0,
                              color: 'primary.main',
                              '&:hover': {
                                backgroundColor: 'transparent',
                                textDecoration: 'underline'
                              }
                            }}
                          >
                            {new URL(opportunity.organization.website).hostname}
                          </Button>
                        </Box>
                      </Stack>
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </Paper>

            {/* AI Match Analysis */}
            {showAIAnalysis && (
              <Paper
                id="ai-analysis-section"
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: 2,
                  bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                  <StarIcon color="primary" />
                  <Typography variant="h6">
                    AI Match Analysis
                  </Typography>
                </Stack>
                
                {isLoadingAI ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                    <CircularProgress size={40} />
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                      Analyzing match...
                    </Typography>
                  </Box>
                ) : aiMatch ? (
                  <Stack spacing={3}>
                    {/* Overall Match Score */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Overall Match Score
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {renderProgressBar(aiMatch.match_score)}
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 45 }}>
                          {Math.round(aiMatch.match_score)}%
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary" mt={1}>
                        {aiMatch.explanation}
                      </Typography>
                    </Box>

                    {/* Match Score Breakdown */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Match Score Breakdown
                      </Typography>
                      <Stack spacing={2}>
                        {renderScoreSection("Skills", aiMatch.skills_match_score, aiMatch.skills_analysis)}
                        {renderScoreSection("Location", aiMatch.location_match_score, aiMatch.location_analysis)}
                        {renderScoreSection("Interests", aiMatch.interests_match_score, aiMatch.career_alignment)}
                      </Stack>
                    </Box>

                    {/* Key Strengths */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Your Strengths
                      </Typography>
                      <Stack spacing={1}>
                        {aiMatch.strengths?.map((strength, index) => (
                          <Typography key={index} variant="body2" color="text.secondary">
                            • {strength}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>

                    {/* Growth Areas */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        Growth Opportunities
                      </Typography>
                      <Stack spacing={1}>
                        {aiMatch.growth_areas?.map((area, index) => (
                          <Typography key={index} variant="body2" color="text.secondary">
                            • {area}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>

                    {/* Recommendations */}
                    <Box>
                      <Typography variant="subtitle2" gutterBottom>
                        How to Improve Your Match
                      </Typography>
                      <Stack spacing={1}>
                        {aiMatch.recommendations?.map((rec, index) => (
                          <Typography key={index} variant="body2" color="text.secondary">
                            • {rec}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                ) : (
                  <Alert severity="error">
                    Failed to load AI analysis. Please try again.
                  </Alert>
                )}
              </Paper>
            )}
          </Stack>
        </Grid>

        <Grid item xs={12} md={4}>
          <Stack spacing={2}>
            {/* Quick Info */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Stack spacing={1.5}>
                <InfoItem
                  icon={<LocationIcon color="action" />}
                  label="Location"
                  value={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Chip 
                        label={opportunity.location_type}
                        size="small"
                        color={getLocationChipColor(opportunity.location_type)}
                        sx={{ 
                          minWidth: 'unset',
                          height: 24,
                          '& .MuiChip-label': {
                            px: 1,
                            fontSize: '0.75rem'
                          }
                        }}
                      />
                      {opportunity.location_type.toLowerCase() !== "remote" && (
                        <Typography variant="body1">
                          {formatAddress(opportunity) || 'Location not specified'}
                        </Typography>
                      )}
                    </Stack>
                  }
                />
                <Divider />
                <InfoItem
                  icon={<AccessTimeIcon color="action" />}
                  label="Commitment"
                  value={`${opportunity?.commitment_hours} hours`}
                />
                <Divider />
                
                <InfoItem
                  icon={<GroupIcon color="action" />}
                  label="Available Spots"
                  value={opportunity?.available_spots}
                />
                {(opportunity?.start_date || opportunity?.end_date) && (
                  <>
                    <Divider />
                    <InfoItem
                      icon={<CalendarIcon color="action" />}
                      label="Dates"
                      value={
                        <>
                          {opportunity?.start_date && new Date(opportunity.start_date).toLocaleDateString()}
                          {opportunity?.end_date && ` - ${new Date(opportunity.end_date).toLocaleDateString()}`}
                        </>
                      }
                    />
                  </>
                )}
                {opportunity?.schedule_description && (
                  <>
                    <Divider />
                    <InfoItem
                      icon={<ScheduleIcon color="action" />}
                      label="Schedule"
                      value={opportunity.schedule_description}
                    />
                  </>
                )}
              </Stack>
            </Paper>

            {/* Application Status */}
            <Paper
              elevation={0}
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: theme => alpha(theme.palette.background.paper, 0.8),
              }}
            >
              <Typography variant="h6" gutterBottom>
                Application Status
              </Typography>
              {existingApplication?.exists ? (
                <Box 
                  onClick={() => existingApplication.application && handleApplicationClick(existingApplication.application.id)}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': {
                      '& .MuiChip-root': {
                        transform: 'scale(1.05)',
                      },
                      '& .MuiTypography-root': {
                        color: 'primary.main',
                      }
                    }
                  }}
                >
                  <Chip
                    label={existingApplication.application?.status || 'Applied'}
                    color={
                      existingApplication.application?.status?.toLowerCase() === 'approved' ? 'success' :
                      existingApplication.application?.status?.toLowerCase() === 'pending' ? 'warning' : 'error'
                    }
                    sx={{ 
                      mb: 1,
                      transition: 'transform 0.2s ease-in-out'
                    }}
                  />
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ transition: 'color 0.2s ease-in-out' }}
                  >
                    Applied on {existingApplication.application ? new Date(existingApplication.application.created_at).toLocaleDateString() : 'Unknown date'}
                  </Typography>
                </Box>
              ) : (
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleApply}
                >
                  Apply Now
                </Button>
              )}
            </Paper>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OpportunityDetailPage; 